
import { useState, useEffect } from 'react';

export default function GallerySection() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const galleryImages = [
    {
      id: 1,
      image: 'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/8cee1295531e76ba282e2a808bd757c5.jpeg',
      title: 'Vista Panorámica',
      description: 'Hermosa vista del mar Caribe desde nuestras instalaciones'
    },
    {
      id: 2,
      image: 'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/06d822755751e3e3a9d8b8b15529f3cf.jpeg',
      title: 'Playa Cristalina',
      description: 'Aguas cristalinas y arena dorada te esperan'
    },
    {
      id: 3,
      image: 'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/92f95d9f86cb36a01f4ccd3a8a1c23d3.jpeg',
      title: 'Naturaleza Virgen',
      description: 'Biodiversidad única del Parque Tayrona'
    },
    {
      id: 4,
      image: 'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/5bde9e0762c1b9c753ace144d8a89901.jpeg',
      title: 'Atardeceres Mágicos',
      description: 'Colores únicos que solo encontrarás aquí'
    },
    {
      id: 5,
      image: 'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/acf65f1d20b7a387551bbbf25a54a81e.jpeg',
      title: 'Comodidades',
      description: 'Instalaciones pensadas para tu confort'
    },
    {
      id: 6,
      image: 'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/71a187f6443d711bad3499c68004a803.jpeg',
      title: 'Experiencias Únicas',
      description: 'Momentos inolvidables en el paraíso'
    },
    {
      id: 7,
      image: 'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/a761020b07c8599e22cb813b979a868d.jpeg',
      title: 'Aventura Natural',
      description: 'Explora senderos y descubre tesoros ocultos'
    },
    {
      id: 8,
      image: 'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/f49f085a9253a98e84c5f5e78131332a.jpeg',
      title: 'Relajación Total',
      description: 'El lugar perfecto para desconectar del mundo'
    }
  ];

  // Auto-play para el carrusel principal
  useEffect(() => {
    if (isAutoPlaying) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlaying, galleryImages.length]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const openLightbox = (imageUrl: string, index: number) => {
    setSelectedImage(imageUrl);
    setCurrentImageIndex(index);
    setIsAutoPlaying(false);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    setIsAutoPlaying(true);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-teal-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-6 py-2 bg-teal-100 rounded-full mb-6">
            <i className="ri-camera-line text-teal-600 mr-2"></i>
            <span className="text-teal-700 font-medium">Galería Visual</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Descubre Nuestro <span className="text-teal-600">Paraíso</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Sumérgete en la belleza natural y las comodidades que te esperan en nuestro refugio tropical
          </p>
        </div>

        {/* Carrusel Principal Destacado */}
        <div className="mb-12">
          <div className="relative max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl group">
            <div className="relative h-96 md:h-[500px]">
              <img 
                src={galleryImages[currentImageIndex].image}
                alt={galleryImages[currentImageIndex].title}
                className="w-full h-full object-cover transition-all duration-700"
              />
              
              {/* Overlay con información */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent">
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-2xl md:text-3xl font-bold mb-2">
                    {galleryImages[currentImageIndex].title}
                  </h3>
                  <p className="text-lg opacity-90">
                    {galleryImages[currentImageIndex].description}
                  </p>
                </div>
              </div>

              {/* Controles de navegación */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 opacity-0 group-hover:opacity-100"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 opacity-0 group-hover:opacity-100"
              >
                <i className="ri-arrow-right-line text-xl"></i>
              </button>

              {/* Control de auto-play */}
              <button
                onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
              >
                <i className={`ri-${isAutoPlaying ? 'pause' : 'play'}-line`}></i>
              </button>
            </div>

            {/* Indicadores de puntos */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentImageIndex 
                      ? 'bg-white scale-125' 
                      : 'bg-white/50 hover:bg-white/75'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Grid de Miniaturas Interactivas */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          {galleryImages.map((item, index) => (
            <div 
              key={item.id}
              className={`relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group ${
                index === currentImageIndex ? 'ring-4 ring-teal-500 scale-105' : ''
              }`}
              onClick={() => openLightbox(item.image, index)}
            >
              <div className="aspect-square">
                <img 
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                
                {/* Overlay con efecto hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-white text-xs font-medium truncate">
                      {item.title}
                    </p>
                  </div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                      <i className="ri-zoom-in-line text-white text-sm"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Estadísticas visuales */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-camera-line text-teal-600 text-xl"></i>
            </div>
            <h4 className="text-2xl font-bold text-gray-800 mb-2">50+</h4>
            <p className="text-gray-600">Fotos Exclusivas</p>
          </div>
          
          <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-landscape-line text-blue-600 text-xl"></i>
            </div>
            <h4 className="text-2xl font-bold text-gray-800 mb-2">8</h4>
            <p className="text-gray-600">Vistas Únicas</p>
          </div>
          
          <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-leaf-line text-green-600 text-xl"></i>
            </div>
            <h4 className="text-2xl font-bold text-gray-800 mb-2">100%</h4>
            <p className="text-gray-600">Naturaleza Pura</p>
          </div>
          
          <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-sun-line text-orange-600 text-xl"></i>
            </div>
            <h4 className="text-2xl font-bold text-gray-800 mb-2">365</h4>
            <p className="text-gray-600">Días de Sol</p>
          </div>
        </div>
      </div>

      {/* Modal Lightbox Mejorado */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-6xl max-h-full">
            {/* Controles del lightbox */}
            <div className="absolute top-4 right-4 flex space-x-2 z-10">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAutoPlaying(!isAutoPlaying);
                }}
                className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
              >
                <i className={`ri-${isAutoPlaying ? 'pause' : 'play'}-line text-xl`}></i>
              </button>
              <button
                onClick={closeLightbox}
                className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Navegación en lightbox */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIndex = (currentImageIndex - 1 + galleryImages.length) % galleryImages.length;
                setCurrentImageIndex(newIndex);
                setSelectedImage(galleryImages[newIndex].image);
              }}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIndex = (currentImageIndex + 1) % galleryImages.length;
                setCurrentImageIndex(newIndex);
                setSelectedImage(galleryImages[newIndex].image);
              }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
            >
              <i className="ri-arrow-right-line text-xl"></i>
            </button>

            {/* Imagen principal */}
            <img 
              src={selectedImage}
              alt="Imagen ampliada"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Información de la imagen */}
            <div className="absolute bottom-4 left-4 right-4 bg-black/50 backdrop-blur-sm rounded-lg p-4 text-white">
              <h3 className="text-xl font-bold mb-2">
                {galleryImages[currentImageIndex].title}
              </h3>
              <p className="text-gray-200">
                {galleryImages[currentImageIndex].description}
              </p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-sm text-gray-300">
                  {currentImageIndex + 1} de {galleryImages.length}
                </span>
                <div className="flex space-x-1">
                  {galleryImages.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full ${
                        index === currentImageIndex ? 'bg-white' : 'bg-white/40'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
